import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import FadeUp from '../components/FadeUp';
import SEO from '../components/SEO';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { toast } from 'sonner';
import { Mail, Send, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Support() {
  const { t, lang } = useLanguage();

  const seoDesc = lang === 'es'
    ? 'Centro de soporte de Anyelo Elite. Preguntas frecuentes sobre reservas, pagos, videoconsultas y más. Contacta con nosotros.'
    : 'Anyelo Elite support center. FAQ about bookings, payments, video consultations and more. Contact us directly.';
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);

  const faqItems = [
    { q: t('faq_1_q'), a: t('faq_1_a') },
    { q: t('faq_2_q'), a: t('faq_2_a') },
    { q: t('faq_3_q'), a: t('faq_3_a') },
    { q: t('faq_4_q'), a: t('faq_4_a') },
    { q: t('faq_5_q'), a: t('faq_5_a') },
    { q: t('faq_6_q'), a: t('faq_6_a') },
    { q: t('faq_7_q'), a: t('faq_7_a') },
  ];

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.subject.trim() || !form.message.trim()) {
      toast.error(t('contact_error'));
      return;
    }
    setSending(true);
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, {
        name: form.name,
        email: form.email,
        subject: form.subject,
        message: form.message,
        honeypot: '',
      });
      toast.success(t('contact_success'));
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      toast.error(t('contact_error'));
    } finally {
      setSending(false);
    }
  };

  return (
    <main style={{ backgroundColor: '#030303', minHeight: '100vh' }}>
      <SEO
        title={lang === 'es' ? 'Soporte' : 'Support'}
        description={seoDesc}
        path="/support"
      />
      {/* Hero */}
      <section className="mx-auto max-w-[1120px] px-4 sm:px-6 lg:px-8 pt-10 pb-6 sm:pt-16 sm:pb-8 md:pt-24 md:pb-12">
        <FadeUp>
          <h1
            className="text-[1.75rem] sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 sm:mb-4"
            style={{ fontFamily: 'Space Grotesk, sans-serif', letterSpacing: '-0.02em' }}
          >
            {t('support_title')}
          </h1>
          <p className="text-white/50 text-sm sm:text-base md:text-lg max-w-2xl" style={{ fontFamily: 'Figtree, sans-serif' }}>
            {t('support_subtitle')}
          </p>
        </FadeUp>
      </section>

      {/* FAQ */}
      <section data-testid="support-faq" className="mx-auto max-w-[1120px] px-4 sm:px-6 lg:px-8 pb-10 sm:pb-16 md:pb-20">
        <FadeUp>
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-5 sm:mb-8" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            {t('support_faq_title')}
          </h2>
        </FadeUp>
        <FadeUp delay={0.1}>
          <div className="rounded-xl sm:rounded-2xl border border-white/10 overflow-hidden" style={{ backgroundColor: '#0A0A0A' }}>
            <Accordion type="single" collapsible className="w-full">
              {faqItems.map((item, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  className="border-b border-white/10 last:border-b-0"
                  data-testid={`support-faq-item-${i}`}
                >
                  <AccordionTrigger
                    className="px-4 sm:px-5 md:px-6 py-3.5 sm:py-4 text-left text-white text-[0.8125rem] sm:text-sm md:text-base font-medium hover:no-underline hover:bg-white/5 transition-colors duration-200"
                    style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                  >
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="px-4 sm:px-5 md:px-6 pb-3.5 sm:pb-4 text-white/60 text-[0.8125rem] sm:text-sm leading-relaxed" style={{ fontFamily: 'Figtree, sans-serif' }}>
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </FadeUp>
      </section>

      {/* Contact Form */}
      <section data-testid="support-contact" className="mx-auto max-w-[1120px] px-4 sm:px-6 lg:px-8 pb-14 sm:pb-20 md:pb-24">
        <div className="lg:grid lg:grid-cols-12 lg:gap-12">
          {/* Left info */}
          <div className="lg:col-span-4 mb-6 sm:mb-8 lg:mb-0">
            <FadeUp>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3 sm:mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {t('support_contact_title')}
              </h2>
              <p className="text-white/50 text-[0.8125rem] sm:text-sm md:text-base leading-relaxed mb-5 sm:mb-6" style={{ fontFamily: 'Figtree, sans-serif' }}>
                {t('support_contact_text')}
              </p>
              <div className="flex flex-col gap-2.5 sm:gap-3">
                <p className="text-white/40 text-[0.8125rem] sm:text-sm">{t('support_email_label')}</p>
                <a
                  href="mailto:support@anyelo.es"
                  className="inline-flex items-center gap-2 text-[0.8125rem] sm:text-sm font-medium transition-colors duration-200 hover:opacity-80"
                  style={{ color: '#C8FF00' }}
                  data-testid="support-email-link"
                >
                  <Mail size={16} className="shrink-0" />
                  support@anyelo.es
                </a>
              </div>
            </FadeUp>
          </div>

          {/* Form */}
          <div className="lg:col-span-8">
            <FadeUp delay={0.1}>
              <form
                onSubmit={handleSubmit}
                className="p-4 sm:p-6 md:p-8 rounded-xl sm:rounded-2xl border border-white/10"
                style={{ backgroundColor: '#0A0A0A' }}
                data-testid="contact-form"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
                  <div>
                    <label className="block text-[0.8125rem] sm:text-sm text-white/70 mb-1 sm:mb-1.5" style={{ fontFamily: 'Figtree, sans-serif' }}>
                      {t('contact_name')}
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder={t('contact_name_placeholder')}
                      data-testid="contact-form-name-input"
                      className="w-full px-3.5 sm:px-4 py-2.5 sm:py-2.5 rounded-lg sm:rounded-xl border border-white/10 text-white text-sm placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[#030303] transition-shadow duration-200"
                      style={{ backgroundColor: '#0F0F10' }}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-[0.8125rem] sm:text-sm text-white/70 mb-1 sm:mb-1.5" style={{ fontFamily: 'Figtree, sans-serif' }}>
                      {t('contact_email')}
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder={t('contact_email_placeholder')}
                      data-testid="contact-form-email-input"
                      className="w-full px-3.5 sm:px-4 py-2.5 sm:py-2.5 rounded-lg sm:rounded-xl border border-white/10 text-white text-sm placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[#030303] transition-shadow duration-200"
                      style={{ backgroundColor: '#0F0F10' }}
                      required
                    />
                  </div>
                </div>
                <div className="mb-3 sm:mb-4">
                  <label className="block text-[0.8125rem] sm:text-sm text-white/70 mb-1 sm:mb-1.5" style={{ fontFamily: 'Figtree, sans-serif' }}>
                    {t('contact_subject')}
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={form.subject}
                    onChange={handleChange}
                    placeholder={t('contact_subject_placeholder')}
                    data-testid="contact-form-subject-input"
                    className="w-full px-3.5 sm:px-4 py-2.5 sm:py-2.5 rounded-lg sm:rounded-xl border border-white/10 text-white text-sm placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[#030303] transition-shadow duration-200"
                    style={{ backgroundColor: '#0F0F10' }}
                    required
                  />
                </div>
                <div className="mb-4 sm:mb-6">
                  <label className="block text-[0.8125rem] sm:text-sm text-white/70 mb-1 sm:mb-1.5" style={{ fontFamily: 'Figtree, sans-serif' }}>
                    {t('contact_message')}
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    placeholder={t('contact_message_placeholder')}
                    rows={4}
                    data-testid="contact-form-message-textarea"
                    className="w-full px-3.5 sm:px-4 py-2.5 sm:py-2.5 rounded-lg sm:rounded-xl border border-white/10 text-white text-sm placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[#030303] transition-shadow duration-200 resize-none"
                    style={{ backgroundColor: '#0F0F10' }}
                    required
                  />
                </div>
                {/* Honeypot - hidden from real users */}
                <input
                  type="text"
                  name="honeypot"
                  className="absolute opacity-0 pointer-events-none"
                  tabIndex={-1}
                  autoComplete="off"
                />
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-form-submit-button"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 sm:py-3 rounded-xl text-sm font-semibold transition-all duration-200 hover:-translate-y-0.5 active:scale-95 disabled:opacity-60 disabled:hover:translate-y-0"
                  style={{ backgroundColor: '#C8FF00', color: '#030303' }}
                >
                  {sending ? (
                    <><Loader2 size={16} className="animate-spin" /> {t('contact_sending')}</>
                  ) : (
                    <><Send size={16} /> {t('contact_submit')}</>
                  )}
                </button>
              </form>
            </FadeUp>
          </div>
        </div>
      </section>
    </main>
  );
}
